// Changing a draft invoice at Review (gap #1): who may add, take off or change a line, when a
// changed price needs a reason, and how a line says it was changed. The server decides; this only
// keeps the screen from offering what it would refuse.

import { money, type InvoiceSummary } from './review-model.ts';

/** A line as invoices.addLine, removeLine and updateLine answer it: the price book's price when it
 * came from there, the reason for a changed price, and whether a signed estimate set it. */
export type InvoiceLine = InvoiceSummary['lines'][number] & {
  bookPriceCents?: number | null;
  overrideReason?: string | null;
  fromEstimate?: boolean;
  /** Priced for a member (M8): the plan and the regular price. */
  member?: { planName: string; listPriceCents: number } | null;
};
export type EditableInvoice = Omit<InvoiceSummary, 'lines'> & {
  lines: InvoiceLine[];
  /** A membership at the address would lower lines still at list (one sold on this visit, M8). */
  memberPricesToApply?: boolean;
};

/** "Member price · Comfort Club · saved $12.35", for a line priced for a member (M8). */
export function memberNote(line: Pick<InvoiceLine, 'quantity' | 'totalCents' | 'member'>): string | null {
  if (!line.member) return null;
  const saved = Math.round(Number(line.quantity) * line.member.listPriceCents) - line.totalCents;
  return `Member price · ${line.member.planName}${saved > 0 ? ` · saved ${money(saved)}` : ''}`;
}

/** A quantity the server takes: like 1 or 1.5, never zero. */
export const quantityOk = (q: string) => /^\d{1,6}(\.\d{1,3})?$/.test(q.trim()) && Number(q) > 0;

/**
 * A changed price says why: when it ends up off the list (a price-book line), or when a custom
 * line's price changes at all.
 */
export function needsReason(
  line: Pick<InvoiceLine, 'unitPriceCents' | 'bookPriceCents'>,
  priceCents: number,
): boolean {
  return line.bookPriceCents != null
    ? priceCents !== line.bookPriceCents
    : priceCents !== line.unitPriceCents;
}

/** "Changed from list $80.00: goodwill, the second visit", for a line priced off the list. */
export function priceNote(
  line: Pick<InvoiceLine, 'unitPriceCents' | 'bookPriceCents' | 'overrideReason'>,
): string | null {
  if (line.bookPriceCents == null || line.bookPriceCents === line.unitPriceCents) return null;
  return `Changed from list ${money(line.bookPriceCents)}${line.overrideReason ? `: ${line.overrideReason}` : ''}`;
}

/** "2 × $45.00" when there's more than one, else nothing. */
export function quantityText(line: Pick<InvoiceLine, 'quantity' | 'unitPriceCents'>): string | null {
  return Number(line.quantity) === 1 ? null : `${Number(line.quantity)} × ${money(line.unitPriceCents)}`;
}
